<template>
  <div class="vc-voice-tone">
    <!-- 大模型 -->
    <div class="vc-section vc-card">
      <div class="vc-section-head vc-section-head--model">
        <span class="vc-section-head__icon vc-section-head__icon--setting" aria-hidden="true">
          <SettingTwoToneIcon :size="16" />
        </span>
        <span class="vc-section-head__title">大模型</span>
      </div>

      <div class="vc-form-field vc-form-field--model">
        <label class="vc-form-label vc-form-label--required">大模型</label>
        <Select
          v-model="modelSelectionKey"
          class="vc-form-control vc-model-select"
          transfer
          transfer-class-name="vc-model-select-transfer"
        >
          <Option
            v-for="item in modelSelectOptions"
            :key="item.value"
            :value="item.value"
            :label="item.label"
          >
            {{ item.label }}
          </Option>
        </Select>
        <p v-if="modelSelectionHint" class="vc-model-select-hint">{{ modelSelectionHint }}</p>
      </div>
    </div>

    <!-- 至尊版：音色与参数 -->
    <div v-if="isIntegratedMode" class="vc-section vc-card">
      <div class="vc-section-head vc-section-head--voice">
        <span class="vc-section-head__icon vc-section-head__icon--voice" aria-hidden="true">
          <svg
            class="vc-voice-params-mic-icon"
            viewBox="0 0 20 20"
            focusable="false"
            width="20"
            height="20"
            fill="none"
            aria-hidden="true"
          >
            <rect x="7.25" y="2" width="5.5" height="9.5" rx="2.75" fill="currentColor" />
            <path
              stroke="currentColor"
              stroke-width="1.4"
              stroke-linecap="round"
              d="M4.5 9.25a5.5 5.5 0 0 0 11 0"
            />
            <path
              stroke="currentColor"
              stroke-width="1.4"
              stroke-linecap="round"
              d="M10 14.75v2.25M7 17h6"
            />
          </svg>
        </span>
        <span class="vc-section-head__title">音色与参数</span>
      </div>

      <div class="vc-form-field">
        <label class="vc-form-label vc-form-label--required">音色</label>
        <div class="vc-voice-select-row">
          <Select
            v-model="voiceCall.presetVoiceId"
            class="vc-form-control vc-voice-preset-select"
            transfer
            transfer-class-name="vc-voice-preset-select-transfer"
            filterable
          >
            <OptionGroup label="系统预设">
              <Option
                v-for="p in systemPresetVoiceOptions"
                :key="'preset-' + p.id"
                :value="p.id"
                :label="p.label"
              >
                {{ p.label }}
              </Option>
            </OptionGroup>
            <OptionGroup v-if="clonedVoiceOptions.length" label="我的音色">
              <Option
                v-for="p in clonedVoiceOptions"
                :key="'clone-' + p.id"
                :value="p.id"
                :label="p.label"
              >
                {{ p.label }}
              </Option>
            </OptionGroup>
          </Select>
          <Button class="vc-my-voices-btn" @click="$emit('open-my-voices')">
            <span class="vc-my-voices-btn__content">
              <Icon type="ios-mic-outline" size="16" />
              <span class="vc-my-voices-btn__label">我的音色</span>
            </span>
          </Button>
        </div>
      </div>

      <SpeechParamsSection :speech-params="voiceCall.speechParams" :max-params="maxSpeechParams" />
    </div>

    <!-- 旗舰版：接入配置 -->
    <div v-else class="vc-section vc-card">
      <div class="vc-section-head vc-section-head--access">
        <span class="vc-section-head__icon vc-section-head__icon--access" aria-hidden="true">
          <Icon type="ios-git-network" size="20" />
        </span>
        <span class="vc-section-head__title">接入配置</span>
      </div>
      <TripleAccessSection
        :triple-access="voiceCall.tripleAccess"
        :product-line="flagshipProductLine"
        :cloned-voices="voiceCall.clonedVoices"
        :tts-speech-params="voiceCall.ttsSpeechParams"
        :asr-speech-params="voiceCall.asrSpeechParams"
        :max-speech-params="maxSpeechParams"
        :max-my-resources="maxMyVoices"
        @open-my-voices="$emit('open-my-voices')"
      />
    </div>
  </div>
</template>

<script>
import TripleAccessSection from './TripleAccessSection.vue'
import SpeechParamsSection from './SpeechParamsSection.vue'
import SettingTwoToneIcon from '../shared/SettingTwoToneIcon.vue'
import {
  PRESET_MODE,
  applySystemLlmCatalogToTripleAccess,
  buildFlagshipModelSelectOptions,
  createDefaultTripleAccess,
  DEFAULT_SYSTEM_LLM_MODEL_ID,
  parseFlagshipModelSelectionKey,
  parseSystemLlmModelIdFromResource,
  resolveTripleAccessProductLine,
} from '../../utils/tripleAccessHelpers'
import { createDefaultSpeechParams } from '../../utils/flowSectionHelpers'
import {
  VOICE_ENGINE,
  getDefaultPresetId,
  getPresetVoiceOptions,
  getPresetVoicesByEngine,
  clonedVoicesToPresetOptions,
} from '../../constants/voicePresets'

const MODEL_SELECTION = Object.freeze({
  ENGINE_ALIYUN: 'engine:aliyun',
  ENGINE_QWEN_OMNI: 'engine:qwen_omni',
  CUSTOM: 'custom',
})

const MODEL_OPTION_LABELS = Object.freeze({
  [MODEL_SELECTION.ENGINE_ALIYUN]: '千问全模态3.5-Omni',
  [MODEL_SELECTION.ENGINE_QWEN_OMNI]: '千问实时语音Audio3.0-Plus',
})

export default {
  name: 'VoiceToneSection',
  components: {
    TripleAccessSection,
    SpeechParamsSection,
    SettingTwoToneIcon,
  },
  props: {
    voiceCall: {
      type: Object,
      required: true,
    },
    maxMyVoices: {
      type: Number,
      default: 5,
    },
    maxSpeechParams: {
      type: Number,
      default: 15,
    },
  },
  data() {
    return {
      VOICE_ENGINE,
      PRESET_MODE,
    }
  },
  computed: {
    presetVoices() {
      return getPresetVoiceOptions(this.voiceCall.voiceEngine, this.voiceCall.clonedVoices)
    },
    systemPresetVoiceOptions() {
      return getPresetVoicesByEngine(this.voiceCall.voiceEngine)
    },
    clonedVoiceOptions() {
      return clonedVoicesToPresetOptions(this.voiceCall.clonedVoices, this.voiceCall.voiceEngine)
    },
    isIntegratedMode() {
      return (this.voiceCall.presetMode || PRESET_MODE.ENGINE) !== PRESET_MODE.CUSTOM
    },
    isCustomMode() {
      return !this.isIntegratedMode
    },
    flagshipProductLine() {
      return resolveTripleAccessProductLine(this.voiceCall.tripleAccess && this.voiceCall.tripleAccess.llm)
    },
    flagshipModelId() {
      const llm = this.voiceCall.tripleAccess && this.voiceCall.tripleAccess.llm
      return (
        parseSystemLlmModelIdFromResource(llm && llm.resource) ||
        String((llm && llm.model) || '').trim()
      )
    },
    flagshipModelOptions() {
      return buildFlagshipModelSelectOptions(this.isCustomMode ? this.flagshipModelId : '')
    },
    modelSelectOptions() {
      return [
        { value: MODEL_SELECTION.ENGINE_ALIYUN, label: MODEL_OPTION_LABELS[MODEL_SELECTION.ENGINE_ALIYUN] },
        { value: MODEL_SELECTION.ENGINE_QWEN_OMNI, label: MODEL_OPTION_LABELS[MODEL_SELECTION.ENGINE_QWEN_OMNI] },
        ...this.flagshipModelOptions,
      ]
    },
    modelSelectionKey: {
      get() {
        if (this.isCustomMode) {
          const hit = this.flagshipModelOptions.find((item) => item.modelId === this.flagshipModelId)
          return hit ? hit.value : this.flagshipModelOptions[0] && this.flagshipModelOptions[0].value
        }
        const engine = String(this.voiceCall.voiceEngine || VOICE_ENGINE.ALIYUN).trim()
        if (engine === VOICE_ENGINE.QWEN_OMNI) return MODEL_SELECTION.ENGINE_QWEN_OMNI
        return MODEL_SELECTION.ENGINE_ALIYUN
      },
      set(key) {
        const next = String(key || '').trim()
        const flagshipId =
          parseFlagshipModelSelectionKey(next) ||
          (next === MODEL_SELECTION.CUSTOM ? DEFAULT_SYSTEM_LLM_MODEL_ID : '')
        if (flagshipId) {
          this.setPresetMode(PRESET_MODE.CUSTOM)
          this.applyFlagshipModel(flagshipId)
          return
        }
        this.setPresetMode(PRESET_MODE.ENGINE)
        const engine =
          next === MODEL_SELECTION.ENGINE_QWEN_OMNI ? VOICE_ENGINE.QWEN_OMNI : VOICE_ENGINE.ALIYUN
        if (this.voiceCall.voiceEngine !== engine) {
          this.$set(this.voiceCall, 'voiceEngine', engine)
        }
        this.$nextTick(() => this.syncPresetVoiceIdToEngine())
      },
    },
    modelSelectionHint() {
      const label =
        (this.modelSelectOptions.find((item) => item.value === this.modelSelectionKey) || {}).label || ''
      if (!label) return ''
      if (this.isCustomMode) {
        return `已选 ${label}：需配置 TTS、ASR 等接入项`
      }
      return `已选 ${label}：配置音色与高级参数即可`
    },
  },
  watch: {
    'voiceCall.voiceEngine'() {
      this.$nextTick(() => this.syncPresetVoiceIdToEngine())
    },
    'voiceCall.clonedVoices': {
      handler() {
        this.$nextTick(() => this.syncPresetVoiceIdToEngine())
      },
      deep: true,
    },
  },
  created() {
    if (!this.voiceCall.presetMode) {
      this.$set(this.voiceCall, 'presetMode', PRESET_MODE.ENGINE)
    }
    if (!this.voiceCall.tripleAccess || typeof this.voiceCall.tripleAccess !== 'object') {
      this.$set(this.voiceCall, 'tripleAccess', createDefaultTripleAccess())
    }
    if (!Array.isArray(this.voiceCall.speechParams)) {
      this.$set(this.voiceCall, 'speechParams', createDefaultSpeechParams())
    }
    if (!Array.isArray(this.voiceCall.ttsSpeechParams)) {
      this.$set(this.voiceCall, 'ttsSpeechParams', createDefaultSpeechParams())
    }
    if (!Array.isArray(this.voiceCall.asrSpeechParams)) {
      this.$set(this.voiceCall, 'asrSpeechParams', createDefaultSpeechParams())
    }
  },
  mounted() {
    this.syncPresetVoiceIdToEngine()
  },
  methods: {
    setPresetMode(mode) {
      const wasCustom = (this.voiceCall.presetMode || PRESET_MODE.ENGINE) === PRESET_MODE.CUSTOM
      this.$set(this.voiceCall, 'presetMode', mode)
      if (
        mode === PRESET_MODE.CUSTOM &&
        !wasCustom &&
        this.voiceCall.callControl &&
        typeof this.voiceCall.callControl === 'object'
      ) {
        this.$set(this.voiceCall.callControl, 'transition_sound_enabled', true)
      }
    },
    applyFlagshipModel(modelId) {
      if (!this.voiceCall.tripleAccess || typeof this.voiceCall.tripleAccess !== 'object') {
        this.$set(this.voiceCall, 'tripleAccess', createDefaultTripleAccess())
      }
      applySystemLlmCatalogToTripleAccess(this.voiceCall.tripleAccess.llm, modelId)
    },
    syncPresetVoiceIdToEngine() {
      const list = this.presetVoices
      if (!list.length) return
      const ids = new Set(list.map((p) => p.id))
      const pv = String(this.voiceCall.presetVoiceId || '').trim()
      if (pv && ids.has(pv)) return
      const fallback = getDefaultPresetId(this.voiceCall.voiceEngine)
      const fallbackId = ids.has(fallback) ? fallback : list[0].id
      this.$set(this.voiceCall, 'presetVoiceId', fallbackId)
    },
  },
}
</script>

<style lang="scss" scoped>
@import './shared.scss';

.vc-voice-tone {
  display: flex;
  flex-direction: column;
}

.vc-section {
  margin-bottom: 16px;
}

.vc-card {
  background: #fff;
  border: 1px solid #e8eaec;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  padding: 20px;
}

.vc-section-head {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 4px;
  margin-bottom: 16px;

  &__icon {
    color: #595959;
    display: inline-flex;
  }

  &__title {
    font-size: 15px;
    font-weight: 600;
    color: #262626;
    text-align: left;
    min-width: 0;
    flex: 0 1 auto;
  }

  &--model &__icon--setting {
    color: #5668f0;
  }

  &--voice &__icon--voice {
    color: #4b7fd4;
  }

  &__icon--voice .vc-voice-params-mic-icon {
    display: block;
  }

  &--access &__icon--access {
    color: #595959;
  }
}

.vc-form-field--model {
  margin-bottom: 0;
}

.vc-model-select-hint {
  margin: 8px 0 0;
  font-size: 12px;
  color: #909399;
  line-height: 1.5;
  text-align: left;
}

.vc-form-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;

  &:last-of-type {
    margin-bottom: 0;
  }
}

.vc-form-label {
  font-size: 14px;
  font-weight: 400;
  color: #606266;
  line-height: 1.4;
  text-align: left;

  &--required::before {
    content: '*';
    color: #ed4014;
    margin-right: 4px;
  }
}

.vc-form-control {
  width: 100%;

  ::v-deep &.ivu-select {
    width: 100%;
  }

  ::v-deep .ivu-select-selection {
    border-color: #dcdfe6;
    border-radius: 4px;
    text-align: left;
  }

  ::v-deep .ivu-select-selected-value {
    text-align: left;
  }

  ::v-deep .ivu-select-input {
    text-align: left;
  }

  ::v-deep .ivu-select-arrow {
    right: 18px;
  }
}

.vc-voice-select-row {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;

  .vc-form-control {
    flex: 1;
    min-width: 0;
  }
}
</style>

<style lang="scss">
/* 与音色等其它 Select 下拉保持同一浅色样式 */
.vc-model-select-transfer {
  .ivu-select-item {
    padding: 7px 30px !important;
  }
}
</style>

<style lang="scss">
.vc-voice-preset-select-transfer {
  .ivu-select-group-title {
    padding-left: 12px;
    font-size: 12px;
    font-weight: 400;
    color: #909399;
    line-height: 28px;
  }

  .ivu-select-item {
    padding: 7px 30px !important;
  }
}
</style>
